import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Chip,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
  Link,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import RightSidebarContainer from '../components/RightSidebarContainer';
import OutlinedButton from '../components/OutlinedButton';
import FilledButton from '../components/FilledButton';
import TopTutorsSidebar from '../components/TopTutorsSidebar';
import CollapsibleSidebarSection from '../components/CollapsibleSidebarSection';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import InstitutionPromptAlert from '../components/InstitutionPromptAlert';
import { Link as RouterLink } from 'react-router-dom';
import {
  fetchSubjects,
  fetchTutors,
  fetchInstitutions,
  fetchTopTutors,
} from '../services/api';
import { useAuth } from '../components/AuthContext';
import { useInstitution } from '../components/InstitutionContext';
import TutorCard from '../components/TutorCard';

const TutorsPage = () => {
  const { user } = useAuth();
  const { selectedInstitution } = useInstitution();
  const [subjects, setSubjects] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [topTutors, setTopTutors] = useState([]);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [institutionName, setInstitutionName] = useState('');
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const tutorProfileId = user?.tutor_profile?.id;

  useEffect(() => {
    const fetchData = async () => {
      const institutionId =
        user?.userprofile?.institution?.id || selectedInstitution;
      if (!institutionId) return;
      try {
        const subjectsData = await fetchSubjects();
        const tutorsData = await fetchTutors(institutionId);
        const topTutorsData = await fetchTopTutors(institutionId);
        setSubjects(subjectsData);
        setTutors(tutorsData);
        setTopTutors(topTutorsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [user, selectedInstitution]);

  useEffect(() => {
    const nameFromUser = user?.userprofile?.institution?.display_name;
    if (nameFromUser) {
      setInstitutionName(nameFromUser);
      return;
    }
    if (!selectedInstitution) {
      setInstitutionName('');
      return;
    }
    const loadInst = async () => {
      try {
        const list = await fetchInstitutions();
        const inst = list.find(
          (i) => String(i.id) === String(selectedInstitution),
        );
        if (inst) setInstitutionName(inst.display_name);
      } catch (err) {
        console.error('Failed to load institution:', err);
      }
    };
    loadInst();
  }, [user, selectedInstitution]);

  const groupedSubjects = subjects.reduce((acc, subject) => {
    const label = subject.category?.label || subject.category;
    if (!acc[label]) acc[label] = [];
    acc[label].push(subject);
    return acc;
  }, {});

  let filteredTutors = selectedSubjectIds.length
    ? tutors.filter((tutor) =>
        selectedSubjectIds.every((id) =>
          tutor.subjects.some((subject) => subject.id === id),
        ),
      )
    : [...tutors];

  if (searchQuery.trim() !== '') {
    const q = searchQuery.toLowerCase();
    filteredTutors = filteredTutors.filter(
      (tutor) =>
        tutor.full_name.toLowerCase().includes(q) ||
        tutor.subjects.some((s) => s.name.toLowerCase().includes(q)),
    );
  }

  if (sortBy === 'name') {
    filteredTutors = [...filteredTutors].sort((a, b) =>
      a.full_name.localeCompare(b.full_name),
    );
  } else if (sortBy === 'hours') {
    filteredTutors = [...filteredTutors].sort(
      (a, b) => (b.total_hours_tutored || 0) - (a.total_hours_tutored || 0),
    );
  }

  return (
    <Box
      sx={{
        maxWidth: '1200px',
        width: '100%',
        mx: 'auto',
        py: 2,
        minHeight: '80vh',
        justifyContent: 'center',
      }}
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        The Tutoring page is under active development, please stay tuned, and
        feel free to explore, and give us suggestions through the{' '}
        <Link component={RouterLink} to="/feedback">
          feedback
        </Link>{' '}
        page
      </Alert>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1, px: 4, py: 4 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <SchoolRoundedIcon color="secondary" />
            Find Your Perfect Tutor
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Connect with tutors and get help with your courses.
          </Typography>
          {!selectedInstitution && (
            <InstitutionPromptAlert
              message="to view available tutors."
              sx={{ mt: 1, mb: 2 }}
            />
          )}
          {institutionName && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 2,
                mb: 2,
              }}
            >
              <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                {institutionName}
              </Typography>
            </Box>
          )}

          {isSmallScreen && (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {tutorProfileId && (
                  <OutlinedButton
                    to={`/tutors/${tutorProfileId}`}
                    text="View My Profile"
                    color="secondary"
                    size="large"
                    sx={{ mb: 1, flexShrink: 0, whiteSpace: 'nowrap' }}
                  />
                )}
              <FilledButton
                to="/tutors/book"
                text="Book a Lesson"
                color="secondary"
                size="large"
                sx={{ mb: 1, flexShrink: 0, whiteSpace: 'nowrap' }}
              />
            </Box>
            <CollapsibleSidebarSection
              title="Top Tutors"
              icon={<EmojiEventsIcon fontSize="small" color="secondary" />}
            >
              <TopTutorsSidebar tutors={topTutors} hideHeader />
            </CollapsibleSidebarSection>
          </>
        )}

        {/* Subject filter chips */}
        <Typography
          variant="h5"
          color="secondary"
          sx={{ mt: 4, mb: 2 }}
        >
          Choose a Subject
        </Typography>
        <Box sx={{ mb: 5 }}>
          {Object.entries(groupedSubjects).map(([categoryLabel, subs]) => (
            <Box key={categoryLabel} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                {categoryLabel}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                    columnGap: 1.5,
                    rowGap: 1.5,
                  }}
                >
                  {subs.map((subject) => {
                    const selected = selectedSubjectIds.includes(subject.id);
                    return (
                      <Chip
                        key={subject.id}
                        label={subject.name}
                        variant="outlined"
                        color="secondary"
                        onClick={() =>
                          setSelectedSubjectIds((prev) =>
                            selected
                              ? prev.filter((id) => id !== subject.id)
                              : [...prev, subject.id],
                          )
                        }
                        onDelete={
                          selected
                            ? () =>
                                setSelectedSubjectIds((prev) =>
                                  prev.filter((id) => id !== subject.id),
                                )
                            : undefined
                        }
                        sx={{
                          borderColor: theme.palette.secondary.light,
                          backgroundColor: selected
                            ? alpha(theme.palette.secondary.light, 0.3)
                            : 'transparent',
                          px: 1,
                          py: 2,
                          '&:hover, &.MuiChip-outlined.MuiChip-colorSecondary:hover': {
                            backgroundColor: selected
                              ? alpha(theme.palette.secondary.light, 0.45)
                              : alpha(theme.palette.secondary.light, 0.1),
                          },
                          '& .MuiChip-label': {
                            fontSize: '0.85rem',
                            fontWeight: 'normal',
                            color: 'text.primary',
                          },
                          ...(selected && {
                            '& .MuiChip-deleteIcon': { fontSize: '1.2rem' },
                          }),
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Search and sort */}
          <Box sx={{ width: '100%', mt: 4, mb: 3 }}>
            {isSmallScreen ? (
              <>
                <Typography variant="h5" sx={{ color: 'secondary.main', mb: 2 }}>
                  {selectedSubjectIds.length
                    ? 'Filtered Tutors'
                    : 'Available Tutors'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <TextField
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search tutors"
                    sx={{ flexGrow: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon
                            sx={{ color: theme.palette.secondary.main }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormControl size="small">
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <MenuItem value="name">Name</MenuItem>
                      <MenuItem value="hours">Hours Tutored</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  gap: 2,
                }}
              >
                <Typography variant="h5" sx={{ color: 'secondary.main' }}>
                  {selectedSubjectIds.length
                    ? 'Filtered Tutors'
                    : 'Available Tutors'}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    ml: 'auto',
                  }}
                >
                  <TextField
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search tutors"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon
                            sx={{ color: theme.palette.secondary.main }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormControl size="small">
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <MenuItem value="name">Name</MenuItem>
                      <MenuItem value="hours">Hours Tutored</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}
          </Box>

          {/* Tutors list */}
          <Box>
            {filteredTutors.map((tutor) => (
              <TutorCard
                key={tutor.id}
                tutor={tutor}
                selectedSubjectIds={selectedSubjectIds}
              />
            ))}
          </Box>
        </Box>
        {!isSmallScreen && (
          <RightSidebarContainer sx={{ ml: 3, mt: 6 }}>
            {tutorProfileId && (
              <OutlinedButton
                to={`/tutors/${tutorProfileId}`}
                text="View My Profile"
                color="secondary"
                size="large"
                sx={{ width: '100%', mb: 1, flexShrink: 0, whiteSpace: 'nowrap' }}
              />
            )}
            <FilledButton
              to="/tutors/book"
              text="Book a Lesson"
              color="secondary"
              size="large"
              sx={{ width: '100%', mb: 1, flexShrink: 0, whiteSpace: 'nowrap' }}
            />
            <TopTutorsSidebar tutors={topTutors} />
          </RightSidebarContainer>
        )}
      </Box>
    </Box>
  );
};

export default TutorsPage;
